import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '@/i18n';
import { applyPwaBranding, getEffectivePwaBrand, type PwaBrand } from '@/utils/pwaBranding';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export type PwaPlatform =
  | 'ios_safari'
  | 'ios_other'
  | 'android_chrome'
  | 'android_other'
  | 'desktop_chromium'
  | 'desktop_other'
  | 'unknown';

let deferredPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', ((e: Event) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  }) as EventListener);
}

function detectPlatform(): PwaPlatform {
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isChrome = /Chrome|CriOS|EdgA|EdgiOS|SamsungBrowser/i.test(ua) && !/OPR|Opera/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|Edg|Android/i.test(ua);
  const isChromiumDesktop =
    !isIOS &&
    !isAndroid &&
    (/Chrome/i.test(ua) || /Edg/i.test(ua) || /Brave/i.test(ua) || /OPR/i.test(ua));

  if (isIOS) return isSafari ? 'ios_safari' : 'ios_other';
  if (isAndroid) return isChrome ? 'android_chrome' : 'android_other';
  if (isChromiumDesktop) return 'desktop_chromium';
  if (!isIOS && !isAndroid) return 'desktop_other';
  return 'unknown';
}

function isStandaloneDisplay(): boolean {
  const mq = window.matchMedia('(display-mode: standalone)');
  // iOS Safari legacy
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return mq.matches || nav.standalone === true;
}

export type UsePWAOptions = {
  /** Company / app display name */
  appName?: string;
  /** Short name for home screen */
  shortName?: string;
  /** Company logo URL (Frappe /files/... or absolute) */
  logoUrl?: string | null;
};

export function usePWA(options: UsePWAOptions = {}) {
  const [isInstallable, setIsInstallable] = useState(() => !!deferredPrompt);
  const [isInstalled, setIsInstalled] = useState(() =>
    typeof window !== 'undefined' ? isStandaloneDisplay() : false
  );
  const [platform, setPlatform] = useState<PwaPlatform>(() =>
    typeof window !== 'undefined' ? detectPlatform() : 'unknown'
  );
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [installError, setInstallError] = useState('');

  const brand: PwaBrand = useMemo(() => {
    // Prefer explicit props, else tenant cache / platform default (do not wipe tenant brand)
    const base = getEffectivePwaBrand();
    return {
      name: options.appName?.trim() || base.name,
      shortName: options.shortName?.trim() || base.shortName,
      logoUrl: options.logoUrl || base.logoUrl,
    };
  }, [options.appName, options.shortName, options.logoUrl]);

  // Apply dynamic manifest + apple-touch-icon whenever branding changes
  useEffect(() => {
    void applyPwaBranding(brand);
  }, [brand]);

  useEffect(() => {
    setPlatform(detectPlatform());
    setIsInstalled(isStandaloneDisplay());

    const onBip = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;
      setIsInstallable(true);
      setInstallError('');
    };
    const onInstalled = () => {
      deferredPrompt = null;
      setIsInstallable(false);
      setIsInstalled(true);
      setShowIosHelp(false);
    };
    const onDisplayMode = () => setIsInstalled(isStandaloneDisplay());

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener?.('change', onDisplayMode);

    // Prompt may have fired before this hook mounted
    if (deferredPrompt) setIsInstallable(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      mq.removeEventListener?.('change', onDisplayMode);
    };
  }, []);

  const canNativePrompt =
    isInstallable &&
    !!deferredPrompt &&
    (platform === 'android_chrome' ||
      platform === 'android_other' ||
      platform === 'desktop_chromium');

  const needsManualInstall =
    !isInstalled &&
    (platform === 'ios_safari' ||
      platform === 'ios_other' ||
      platform === 'desktop_other' ||
      (!canNativePrompt && !isInstallable));

  const installPWA = useCallback(async () => {
    setInstallError('');
    if (isInstalled) return;

    // iOS / browsers without beforeinstallprompt → show how-to
    if (platform === 'ios_safari' || platform === 'ios_other') {
      setShowIosHelp(true);
      return;
    }

    // Chrome/Edge may fire beforeinstallprompt a moment after load — wait briefly
    if (!deferredPrompt) {
      const deadline = Date.now() + 4000;
      while (!deferredPrompt && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 250));
        if (deferredPrompt) setIsInstallable(true);
      }
    }

    if (!deferredPrompt) {
      setShowIosHelp(true);
      setInstallError(
        platform === 'desktop_other'
          ? i18n.t('pwa.errorUseChrome')
          : i18n.t('pwa.errorNotReady'),
      );
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      deferredPrompt = null;
      setIsInstallable(false);
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
    } catch (err) {
      setInstallError(err instanceof Error ? err.message : i18n.t('pwa.errorFailed'));
    }
  }, [isInstalled, platform]);

  return {
    isInstallable: canNativePrompt,
    isInstalled,
    platform,
    needsManualInstall,
    showIosHelp,
    setShowIosHelp,
    installError,
    installPWA,
    brand,
  };
}
